import { ElementProps } from '@/components/sections/builder/types';
import { ImageGalleryElementDataType } from '@/components/sections/builder/elements/body/image-gallery/type';
import { WrapperChild } from '@/components/sections/builder/wrapper';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import clsx from 'clsx';
import Autoplay from 'embla-carousel-autoplay';
import { buildBackgroundWithOpacity } from '@/components/sections/builder/settings/background-setting';
import { ElementType } from '@/stores/builder';

export default function ElementContent({ element, preview }: ElementProps) {
  const data = element.data as ImageGalleryElementDataType;

  const childs = element.childs || [];

  const masonryChilds = childs.reduce(
    (result, child, index) => {
      result[index % 4].push(child);
      return result;
    },
    [[], [], [], []] as ElementType[][]
  );

  return (
    <>
      <section
        className={clsx(
          'bg-white',
          `bg-opacity-[10%]`,
          data.background?.type === 'color' && 'background-color',
          data.background?.type === 'image' && 'background-image',
          data.background?.backgroundLayout === 'grid' && 'theme_layout_page_width mx-auto'
        )}
      >
        <div className='theme_layout_page_width mx-auto duration-100'>
          <div
            className={clsx(
              'padding',
              data.contentAlign === 'left' && 'text-left',
              data.contentAlign === 'center' && 'text-center',
              data.contentAlign === 'right' && 'text-right'
            )}
          >
            <div className='relative px-6 py-6 lg:py-8'>
              <h2
                className={clsx(
                  'text-md text_color theme_font_heading tracking-tight',
                  data.headingSize === 'large' && 'text-4xl @sm:text-4xl md:text-5xl lg:text-6xl',
                  data.headingSize === 'medium' && 'text-2xl @sm:text-2xl md:text-3xl lg:text-4xl',
                  data.headingSize === 'small' && 'text-xl @sm:text-xl md:text-2xl lg:text-3xl'
                )}
                data-animation='true'
                dangerouslySetInnerHTML={{ __html: data.headingLabel }}
              />
              <p
                className={clsx(
                  'text_secondary_color mb-8',
                  data.textStyle === 'body' && 'text-base md:text-lg lg:text-xl',
                  data.textStyle === 'subtitle' && 'text-sm md:text-base lg:text-lg',
                  data.textStyle === 'uppercase' && 'text-xs uppercase md:text-sm lg:text-base'
                )}
                data-animation='true'
                dangerouslySetInnerHTML={{ __html: data.textLabel as string }}
              />
              {data.layout === 'grid' && (
                <div
                  className={clsx(
                    'grid items-start gap-4',
                    data.size === 'small' && 'desktop-grid-col grid-cols-2 md:grid-cols-3',
                    data.size === 'medium' && 'desktop-grid-col grid-cols-1 md:grid-cols-2',
                    data.size === 'large' && 'desktop-grid-col grid-cols-1 md:grid-cols-2'
                  )}
                >
                  {childs.map((child) => (
                    <div key={child.id}>
                      <WrapperChild
                        child={{
                          ...child,
                          parentData: data,
                        }}
                        parentType={element.type}
                        preview={preview}
                        className='h-full w-full'
                      />
                    </div>
                  ))}
                </div>
              )}
              {data.layout === 'masonry' && (
                <div className='grid grid-cols-2 items-start gap-4 md:grid-cols-4'>
                  {masonryChilds.map((childs, index) => (
                    <div className='grid gap-4' key={`col-${index}`}>
                      {childs.map((child) => (
                        <WrapperChild
                          child={child}
                          key={child.id}
                          parentType={element.type}
                          preview={preview}
                          className='h-full w-full'
                        />
                      ))}
                    </div>
                  ))}
                </div>
              )}
              {data.layout === 'carousel' && (
                <Carousel
                  plugins={
                    data.changeEvery
                      ? [
                          Autoplay({
                            playOnInit: true,
                            delay: data.changeEvery * 1000,
                          }),
                        ]
                      : []
                  }
                  className='mx-auto max-w-[80%] md:max-w-[60%]'
                  opts={{ loop: true }}
                >
                  {!!data.showIcon && (
                    <CarouselPrevious className='md:-left-14 md:h-12 md:w-12 md:[&>svg]:h-6 md:[&>svg]:w-6' />
                  )}
                  <CarouselContent>
                    {childs.map((child) => (
                      <CarouselItem key={child.id}>
                        <WrapperChild
                          child={{
                            ...child,
                            parentData: data,
                          }}
                          parentType={element.type}
                          preview={preview}
                          className='h-full w-full'
                        />
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                  {!!data.showIcon && (
                    <CarouselNext className='md:-right-14 md:h-12 md:w-12 md:[&>svg]:h-6 md:[&>svg]:w-6' />
                  )}
                </Carousel>
              )}
            </div>
          </div>
        </div>
      </section>
      <style jsx>
        {`
          @media (min-width: 1024px) {
            .desktop-grid-col {
              grid-template-columns: repeat(${data.totalCol || 1}, minmax(0, 1fr));
            }
          }
          @media (min-width: 768px) {
            .padding {
              padding-top: ${data.padding?.top || 0}px;
              padding-bottom: ${data.padding?.bottom || 0}px;
              padding-left: ${data.padding?.left || 0}px;
              padding-right: ${data.padding?.right || 0}px;
            }
          }
          .text_color {
            color: ${data.sectionColorScheme?.text || '#111827'};
          }
          .text_secondary_color {
            color: ${data.sectionColorScheme?.textSecondary || '#111827'};
          }
          .background-color {
            background-color: ${buildBackgroundWithOpacity(
              data.background?.backgroundColor || (data.sectionColorScheme?.background as string),
              Number(data.background?.backgroundOpacity || 100)
            )};
          }
          .background-image {
            background-image: url(${data.background?.backgroundImage});
            background-repeat: no-repeat;
            background-size: ${data.background?.backgroundSize};
            background-attachment: ${data.background?.backgroundAttachment};
            background-position: center center;
          }
        `}
      </style>
    </>
  );
}
